// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";


import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";


// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

import { Dialog } from "ui/components/Dialog";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { Errors } from "services/forms/validation/types";
import { InvoiceRentRow, InvoiceOutgoingSearchFormValues } from "services/api/fleet/vehicles/invoices/types";
import { initialFleetVehicleInvoiceSearchForm } from "services/api/fleet/vehicles/invoices/consts";
import { VehicleInvoiceSearchPageCmp, VehicleInvoiceSearchPageProps } from "../../types";
import { deleteFleetVehicleInvoiceRentAPI, getFleetVehicleInvoiceRentsAPI } from "services/api/fleet/vehicles/invoices/api";

import VehicleInvoiceRentEditCard from "../../../VehicleInvoiceRentEditCard/VehicleInvoiceRentEditCard";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import VehicleInvoiceRentSearchFilters from "../VehicleInvoiceRentSearchFilters/VehicleInvoiceRentSearchFilters";
import VehicleInvoiceRentSearchResults from "../VehicleInvoiceRentSearchResults/VehicleInvoiceRentSearchResults";

const VehicleInvoiceRentSearchTab: VehicleInvoiceSearchPageCmp = (
    props: VehicleInvoiceSearchPageProps
) => {
    const { id } = useParams();

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
        initialFleetVehicleDetailForm
    );

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<InvoiceRentRow>
    >(initialDataWithPagination);

    const [filters, setFilters] = useState<InvoiceOutgoingSearchFormValues>(
        initialFleetVehicleInvoiceSearchForm
    );

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [invoiceId, setInvoiceId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<Errors>({});


    useEffect(() => {
        if (id) {
            fetchVehicle(id);
            fetchSearchResult(id, searchResults.pagination);
        }
    }, [id]);



    const fetchVehicle = async (id: string) => {
        if (id === undefined) {
            setFormValues(initialFleetVehicleDetailForm);
            return;
        }

        setIsLoading(true);

        let result;
        try {
            result = await getFleetVehicleAPI(id);
        } catch {
            setIsLoading(false);
            return;
        }

        setFormValues(result);
        setIsLoading(false);

        setErrors({});
    };

    const fetchSearchResult = async (id: string, pagination: Pagination, filterValues?: InvoiceOutgoingSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleInvoiceRentsAPI(pagination, {
                vehicleId: id,
                invoiceStatusId: f.invoiceStatusId ?? undefined,
                search: f.search ?? undefined,
                invoiceDateFrom: f.invoiceDateFrom,
                invoiceDateTo: f.invoiceDateTo,
                dueDateFrom: f.dueDateFrom,
                dueDateTo: f.dueDateTo,
                totalMin: f.totalMin,
                totalMax: f.totalMax,
                contactClientId: f.contactClientId ?? undefined,
            });

            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setInvoiceId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setInvoiceId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        if (id) fetchSearchResult(id, searchResults.pagination);

        setInvoiceId("");
        setShowAddNewModal(false);
    }, [id, searchResults.pagination]);

    const handleSearchChange = useCallback((formValues: InvoiceOutgoingSearchFormValues) => {
        setFilters(formValues);
        if (id) fetchSearchResult(id, searchResults.pagination, formValues);
    }, [id, searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        if (id) fetchSearchResult(id, newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        setInvoiceId(id);
        setOpenDialog(true);
    }

    const handleInvoiceDeleteDialogConfirm = useCallback(async () => {
        if (!invoiceId || !id) return;

        try {
            const deleted = await deleteFleetVehicleInvoiceRentAPI(id, invoiceId, t);

            if (deleted && id) {
                await fetchSearchResult(id, searchResults.pagination);
            }

            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setInvoiceId("");
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [invoiceId, t]);

    const handleInvoiceDeleteDialogClose = useCallback(() => {
        setInvoiceId("");
        setOpenDialog(false);
    }, []);


    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }



    if (!id) return <></>;

    return (
        <>

             <div className="px-0 py-6 mx-[-1px] mt-[-1px] ">
                <div className="text-xl font-bold text-slate-800">
                     Računi za najam
                </div>
                <VehicleInvoiceRentSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite dokument klikom na 'Dodaj račun'" />
                ) : (
                    <VehicleInvoiceRentSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>

            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.invoice.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.invoice.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.invoice.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.invoice.dialog.delete.canceltext"
                )}
                onConfirm={handleInvoiceDeleteDialogConfirm}
                onCancel={handleInvoiceDeleteDialogClose}
            />
            <VehicleInvoiceRentEditCard
                show={showAddNewModal}
                vehicleId={id}
                invoiceId={invoiceId}
                onSuccess={handleEditSuccess}
                onClose={handleEditSuccess}
            />
        </>
    );
};

VehicleInvoiceRentSearchTab.route =
    FleetVehicleRoutes.FleetVehicleInvoiceSearchPageRouter;

export default VehicleInvoiceRentSearchTab;
